// src/components/layout/MobileBottomNav.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, X, Home, Store, ArrowLeftRight, Mail } from 'lucide-react';

const MobileBottomNav = () => {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  // Refs for scroll handling
  const scrollTimeoutRef = useRef(null);
  const lastScrollDirectionRef = useRef('down');
  const mainContentRef = useRef(null);

  // Find the main content element that actually scrolls
  const getScrollContainer = useCallback(() => {
    // Try to find the main content container
    const mainContent = document.querySelector('main');
    const contentArea = document.querySelector('[class*="overflow-y-auto"]');
    return mainContent || contentArea || document.documentElement;
  }, []);

  // Scroll handler with proper mobile behavior
  const handleScroll = useCallback(() => {
    const scrollContainer = getScrollContainer();
    const currentScrollY = scrollContainer.scrollTop || document.documentElement.scrollTop || document.body.scrollTop;
    const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';
    
    // Handle mobile nav visibility
    if (window.innerWidth < 768) { // md breakpoint
      if (scrollDirection === 'down' && currentScrollY > 100) {
        // Scrolling down - hide nav
        setIsNavVisible(false);
      } else if (scrollDirection === 'up') {
        // Scrolling up - show nav
        setIsNavVisible(true);
      }

      // Always show nav at the top of the page
      if (currentScrollY < 50) {
        setIsNavVisible(true);
      }
    } else {
      // Always visible on desktop
      setIsNavVisible(true);
    }

    setLastScrollY(currentScrollY);
    lastScrollDirectionRef.current = scrollDirection;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Auto-show nav when scrolling stops (optional)
    scrollTimeoutRef.current = setTimeout(() => {
      if (window.innerWidth < 768 && lastScrollDirectionRef.current === 'down' && currentScrollY > 100) {
        setIsNavVisible(true);
      }
    }, 1500);
  }, [lastScrollY, getScrollContainer]);

  // Scroll event listener - listen to multiple possible scroll containers
  useEffect(() => {
    const scrollContainer = getScrollContainer();
    
    const handleScrollEvent = () => {
      handleScroll();
    };

    // Listen to scroll on the actual container that scrolls
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScrollEvent, { passive: true });
    }

    // Also listen to window scroll as fallback
    window.addEventListener('scroll', handleScrollEvent, { passive: true });
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScrollEvent);
      }
      window.removeEventListener('scroll', handleScrollEvent);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, getScrollContainer]);

  // Handle body scroll when menu is open
  useEffect(() => {
    if (showQuickActions) {
      document.body.classList.add('overflow-hidden');
      document.documentElement.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, [showQuickActions]);

  // Close quick actions when route changes
  useEffect(() => {
    setShowQuickActions(false);
  }, [location.pathname]);

  // Close quick actions when nav hides
  useEffect(() => {
    if (!isNavVisible) {
      setShowQuickActions(false);
    }
  }, [isNavVisible]);

  // Reset nav visibility when route changes
  useEffect(() => {
    setIsNavVisible(true);
  }, [location.pathname]);

  // ... rest of your icon mapping and component code remains the same ...
  const getIconComponent = (iconName, className = "w-5 h-5") => {
    const iconMap = {
      'dashboard': (
        <Home className={className} />
      ),
      'store': (
        <Store className={className} />
      ),
      'plus': (
        <Plus className="w-5 h-5" />
      ),
      'transactions': (
        <ArrowLeftRight className={className} />
      ),
      'invoice': (
   <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="48" height="48" rx="24" fill="none"></rect><rect x="16" y="14" width="16" height="20" rx="4" stroke="currentColor" strokeWidth="1.5"></rect><path d="M20 19H28" stroke="currentColor" strokeWidth="1.5" stroke-linecap="round"></path><path d="M20 24H28" stroke="currentColor" strokeWidth="1.5" stroke-linecap="round"></path><path d="M20 29H24" stroke="currentColor" strokeWidth="1.5" stroke-linecap="round"></path></svg>
      ),
      'invoice-plus': (
        <svg className={className} fill="currentColor" width="24" height="24" viewBox="0 0 16 16">
  <path d="M8 6.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V11a.5.5 0 0 1-1 0V9.5H6a.5.5 0 0 1 0-1h1.5V7a.5.5 0 0 1 .5-.5"/>
  <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z"/>
</svg>
      ),
     'send-money': (
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-send" viewBox="0 0 16 16">
  <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/>
</svg>
     ),
           'meo': (
<svg className={className} fill="currentColor" viewBox="0 0 50 50">
<path d="M22.462 11.035l2.88 7.097c1.204 2.968 3.558 5.322 6.526 6.526l7.097 2.88c1.312.533 1.312 2.391 0 2.923l-7.097 2.88c-2.968 1.204-5.322 3.558-6.526 6.526l-2.88 7.097c-.533 1.312-2.391 1.312-2.923 0l-2.88-7.097c-1.204-2.968-3.558-5.322-6.526-6.526l-7.097-2.88c-1.312-.533-1.312-2.391 0-2.923l7.097-2.88c2.968-1.204 5.322-3.558 6.526-6.526l2.88-7.097C20.071 9.723 21.929 9.723 22.462 11.035zM39.945 2.701l.842 2.428c.664 1.915 2.169 3.42 4.084 4.084l2.428.842c.896.311.896 1.578 0 1.889l-2.428.842c-1.915.664-3.42 2.169-4.084 4.084l-.842 2.428c-.311.896-1.578.896-1.889 0l-.842-2.428c-.664-1.915-2.169-3.42-4.084-4.084l-2.428-.842c-.896-.311-.896-1.578 0-1.889l2.428-.842c1.915-.664 3.42-2.169 4.084-4.084l.842-2.428C38.366 1.805 39.634 1.805 39.945 2.701z"></path>
</svg>), 
'wallet': (<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"><g fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"><path d="M21.1 8.004C21.045 8 20.984 8 20.92 8h-2.525c-2.068 0-3.837 1.628-3.837 3.75s1.77 3.75 3.837 3.75h2.525c.064 0 .125 0 .182-.004a1.755 1.755 0 0 0 1.645-1.628c.004-.06.004-.125.004-.185V9.817c0-.06 0-.125-.004-.185a1.755 1.755 0 0 0-1.645-1.628Zm-2.928 4.746c.532 0 .963-.448.963-1s-.431-1-.963-1c-.533 0-.964.448-.964 1s.431 1 .964 1Z"/><path d="M20.918 17a.22.22 0 0 1 .221.278c-.2.712-.519 1.32-1.03 1.83c-.749.75-1.698 1.081-2.87 1.239c-1.14.153-2.595.153-4.433.153h-2.112c-1.838 0-3.294 0-4.433-.153c-1.172-.158-2.121-.49-2.87-1.238c-.748-.749-1.08-1.698-1.238-2.87C2 15.099 2 13.644 2 11.806v-.112C2 9.856 2 8.4 2.153 7.26c.158-1.172.49-2.121 1.238-2.87c.749-.748 1.698-1.08 2.87-1.238C7.401 3 8.856 3 10.694 3h2.112c1.838 0 3.294 0 4.433.153c1.172.158 2.121.49 2.87 1.238c.511.512.83 1.119 1.03 1.831a.22.22 0 0 1-.221.278h-2.524c-2.837 0-5.337 2.24-5.337 5.25s2.5 5.25 5.337 5.25h2.524ZM7 15.5a.75.75 0 0 1-.75-.75v-6a.75.75 0 0 1 1.5 0v6a.75.75 0 0 1-.75.75Z"fill="currentColor"/></g></svg>),
      'order': (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><path fill="currentColor" fill-rule="evenodd" d="M8.207 1.128a.5.5 0 0 0-.414 0L1.377 4.045a.5.5 0 0 0-.294.455v6.417a.5.5 0 0 0 .261.439l6.417 3.5a.5.5 0 0 0 .479 0l1.76-.96V9.5h1v3.85l1-.545V8.5h1v3.759l1.656-.903a.5.5 0 0 0 .26-.44V4.5a.5.5 0 0 0-.292-.455zM2.083 10.62V5.277L7.5 7.739v5.835zM8 6.867L13.21 4.5L8 2.133L2.792 4.5z" clip-rule="evenodd"/></svg>),
      'message': (<Mail className={className} />),
      
      'add-product': (
        <svg  className={className} fill="currentColor" viewBox="0 0 16 16" width="24" height="24">
  <path fillRule="evenodd" d="M8 7.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V12a.5.5 0 0 1-1 0v-1.5H6a.5.5 0 0 1 0-1h1.5V8a.5.5 0 0 1 .5-.5"/>
  <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/>
</svg>
      ),
      'add-customer': (
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><path fill="currentColor" d="M7.9 8.548h-.001a5.528 5.528 0 0 1 3.1 4.659a.75.75 0 1 1-1.498.086A4.01 4.01 0 0 0 5.5 9.5a4.01 4.01 0 0 0-4.001 3.793a.75.75 0 1 1-1.498-.085a5.527 5.527 0 0 1 3.1-4.66a3.5 3.5 0 1 1 4.799 0ZM13.25 0a.75.75 0 0 1 .75.75V2h1.25a.75.75 0 0 1 0 1.5H14v1.25a.75.75 0 0 1-1.5 0V3.5h-1.25a.75.75 0 0 1 0-1.5h1.25V.75a.75.75 0 0 1 .75-.75ZM5.5 4a2 2 0 1 0-.001 3.999A2 2 0 0 0 5.5 4Z"/></svg>
      ),
      'request-money': (
        <svg className={className} fill="currentColor" viewBox="0 0 16 16" width="24" height="24">
  <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5z"/>
</svg>
      )
    };

    return iconMap[iconName] || (
      <svg className={className} fill="currentColor" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="8"/>
      </svg>
    );
  };

  const navItems = [
    { path: "/dashboard", label: "Home", icon: "dashboard" },
    { path: "/store", label: "Store", icon: "store" },
    { path: "#quick-actions", label: "Create", icon: "plus", isAction: true },
    { path: "/transactions", label: "Transactions", icon: "transactions" },
    { path: "/chats", label: "Chats", icon: "message" },
  ];

  const quickActions = [
    { path: "/store#create", label: "Add Product", icon: "add-product" },
    { path: "/store#customers", label: "Add Customer", icon: "add-customer" },
    { path: "/wallet#send-money", label: "Send Money", icon: "send-money", comingSoon: true },
    { path: "/wallet#request-money", label: "Request Money", icon: "request-money" },
    { path: "/store#orders", label: "Manage Orders", icon: "order" },
    { path: "/new/invoice", label: "Create Invoice", icon: "invoice-plus" },
    { path: "/invoices", label: "Invoices", icon: "invoice" },
    { path: "/settings#meo-voice", label: "Meo AI", icon: "meo" },
  ];

  const toggleQuickActions = () => {
    setShowQuickActions(!showQuickActions);
  };

  return (
    <>
      {/* Quick Actions Overlay */}
      {showQuickActions && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden touch-none"
          onClick={() => setShowQuickActions(false)}
        />
      )}

      {/* Quick Actions Menu */}
      <div 
        className={`fixed bottom-0 inset-x-0 bg-white dark:bg-black rounded-t-3xl z-50 transform transition-all duration-300 ease-out lg:hidden shadow-2xl ${
          showQuickActions ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ maxHeight: '70vh' }}
      >
        {/* Header with close button */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-lg font-bold text-black dark:text-white">Quick Actions</h3>
          <button
            onClick={() => setShowQuickActions(false)}
            className="p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors active:scale-95"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        <div className="p-5 overflow-y-auto pb-16" style={{ maxHeight: 'calc(70vh - 70px)' }}>
          <div className="grid grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                onClick={() => setShowQuickActions(false)}
                className={`flex flex-col items-center transition-all ${
                  action.comingSoon 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'active:scale-95'
                }`}
                aria-disabled={action.comingSoon}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${
                  action.comingSoon 
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600' 
                    : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                }`}>
                  {getIconComponent(action.icon, "w-6 h-6")}
                </div>
                <span className="text-xs text-center font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {action.label}
                  {action.comingSoon && (
                    <span className="block text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Coming soon</span>
                  )}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <nav 
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-[60] transition-transform duration-250 ease-out safe-area-bottom ${
          isNavVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white/80 dark:bg-black/80 backdrop-blur-xl border-t border-gray-200/70 dark:border-gray-800/70">
          <div className="flex items-center justify-around max-w-lg mx-auto px-2 h-12 pb-1.5">
            {navItems.map((item) => (
              item.isAction ? (
                <button
                  key={item.label}
                  onClick={toggleQuickActions}
                  className="relative flex items-center justify-center w-11 h-9"
                  aria-label="Quick Actions"
                  aria-expanded={showQuickActions}
                >
                  <div className={`w-11 h-9 flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg transition-all duration-150 ${
                    showQuickActions ? 'rotate-45 scale-110' : 'active:scale-90'
                  }`}>
                    {getIconComponent(item.icon, "w-5 h-5")}
                  </div>
                </button>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex flex-col items-center justify-center w-14 h-10 rounded-xl active:scale-90 transition-transform duration-150 ${
                    location.pathname === item.path 
                      ? 'text-blue-500 dark:text-white' 
                      : 'text-gray-700 dark:text-gray-400'
                  }`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  <div className="relative flex items-center justify-center">
                    {getIconComponent(item.icon, "w-6 h-6")}
                  </div>
                </Link>
              )
            ))}
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind nav */}
      <div className="h-12 lg:hidden" />

      {/* Add some CSS for safe areas */}
      <style jsx>{`
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom);
        }
      `}</style>
    </>
  );
};

export default MobileBottomNav;